import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { claimEmailsApi } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Envelope, PaperPlaneTilt, ArrowsClockwise, WarningCircle, CheckCircle,
  Clock, EnvelopeOpen, Eye, Link, Gear, MagnifyingGlass, Download,
} from '@phosphor-icons/react';
import { Toaster, toast } from 'sonner';

const EmailDashboardPage = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('dashboard'); // dashboard | incoming | settings
  const [dashboard, setDashboard] = useState(null);
  const [incoming, setIncoming] = useState([]);
  const [incomingTotal, setIncomingTotal] = useState(0);
  const [imapConfig, setImapConfig] = useState({ imap_host: 'imap.gmail.com', imap_port: 993, imap_user: '', imap_password: '', imap_use_ssl: true });
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [filters, setFilters] = useState({ linked: '', is_read: '', search: '' });

  useEffect(() => { loadDashboard(); }, []);

  const loadDashboard = async () => {
    setLoading(true);
    try { const res = await claimEmailsApi.dashboard(); setDashboard(res.data); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const loadIncoming = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.linked) params.linked = filters.linked;
      if (filters.is_read) params.is_read = filters.is_read;
      if (filters.search) params.search = filters.search;
      const res = await claimEmailsApi.incoming(params);
      setIncoming(res.data.emails || []);
      setIncomingTotal(res.data.total || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [filters]);

  const loadImapConfig = async () => {
    try {
      const res = await claimEmailsApi.imapConfig();
      setImapConfig(res.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { if (tab === 'incoming') loadIncoming(); }, [tab, loadIncoming]);
  useEffect(() => { if (tab === 'settings') loadImapConfig(); }, [tab]);

  const handleFetchEmails = async () => {
    setFetching(true);
    try {
      const res = await claimEmailsApi.fetchIncoming();
      toast.success(res.data.message);
      loadDashboard();
      if (tab === 'incoming') loadIncoming();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fetch failed'); }
    finally { setFetching(false); }
  };

  const handleTestImap = async () => {
    setTesting(true);
    try {
      const res = await claimEmailsApi.testImap();
      if (res.data.success) toast.success(res.data.message);
      else toast.error(res.data.message);
    } catch (e) { toast.error(e.response?.data?.detail || 'Test failed'); }
    finally { setTesting(false); }
  };

  const handleSaveImap = async () => {
    setSaving(true);
    try {
      await claimEmailsApi.updateImapConfig(imapConfig);
      toast.success('IMAP configuration saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleMarkRead = async (emailId) => {
    try {
      await claimEmailsApi.markRead(emailId);
      setIncoming(prev => prev.map(e => e.id === emailId ? { ...e, is_read: true } : e));
      loadDashboard();
    } catch {}
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const d = dashboard || {};

  return (
    <div className="space-y-5" data-testid="email-dashboard-page">
      <Toaster position="top-right" richColors />

      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-900 via-slate-900 to-violet-900 rounded-2xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-indigo-500/20 flex items-center justify-center border border-indigo-500/30">
              <Envelope className="h-6 w-6 text-indigo-400" weight="duotone" />
            </div>
            <div>
              <h1 className="text-xl font-bold font-['Outfit']">Email Communication Center</h1>
              <p className="text-sm text-slate-300 mt-0.5">Manage claim emails, incoming TPA replies, and notifications</p>
            </div>
          </div>
          <Button onClick={handleFetchEmails} disabled={fetching}
            className="bg-emerald-500 hover:bg-emerald-600 text-white gap-1.5 text-sm" data-testid="fetch-emails-btn">
            <Download className="h-4 w-4" /> {fetching ? 'Fetching...' : 'Fetch Incoming Emails'}
          </Button>
        </div>
      </div>

      {/* Tab Buttons */}
      <div className="flex gap-2">
        {[
          { key: 'dashboard', label: 'Dashboard', icon: Envelope },
          { key: 'incoming', label: `Incoming ${d.unread ? `(${d.unread})` : ''}`, icon: EnvelopeOpen },
          { key: 'settings', label: 'IMAP Settings', icon: Gear },
        ].map(t => (
          <Button key={t.key} variant={tab === t.key ? 'default' : 'outline'} size="sm"
            className={`gap-1.5 text-xs ${tab === t.key ? 'bg-indigo-500 text-white' : ''}`}
            onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}>
            <t.icon className="h-3.5 w-3.5" /> {t.label}
          </Button>
        ))}
      </div>

      {/* ========== DASHBOARD TAB ========== */}
      {tab === 'dashboard' && (
        <div className="space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {[
              { label: 'Total Sent', value: d.total_sent, color: 'text-indigo-600', bg: 'bg-indigo-50', icon: PaperPlaneTilt },
              { label: 'Sent Today', value: d.sent_today, color: 'text-blue-600', bg: 'bg-blue-50', icon: Clock },
              { label: 'Failed', value: d.sent_failed, color: 'text-red-600', bg: 'bg-red-50', icon: WarningCircle },
              { label: 'Total Incoming', value: d.total_incoming, color: 'text-teal-600', bg: 'bg-teal-50', icon: EnvelopeOpen },
              { label: 'Incoming Today', value: d.incoming_today, color: 'text-emerald-600', bg: 'bg-emerald-50', icon: Download },
              { label: 'Unread', value: d.unread, color: 'text-amber-600', bg: 'bg-amber-50', icon: Envelope },
              { label: 'Unlinked', value: d.unlinked, color: 'text-orange-600', bg: 'bg-orange-50', icon: Link },
            ].map((s, i) => (
              <Card key={i} className={`${s.bg} border-0 shadow-sm`}><CardContent className="p-3">
                <p className="text-[9px] uppercase tracking-wider font-semibold text-slate-500">{s.label}</p>
                <div className="flex items-center justify-between mt-1">
                  <p className={`text-xl font-bold font-['Outfit'] ${s.color}`}>{s.value || 0}</p>
                  <s.icon className={`h-5 w-5 ${s.color}`} weight="duotone" />
                </div>
              </CardContent></Card>
            ))}
          </div>

          {/* Recent Emails */}
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold font-['Outfit'] text-slate-800">Recent Emails</CardTitle></CardHeader>
            <CardContent className="p-0">
              {(d.recent_emails || []).length === 0 ? (
                <div className="text-center py-8 text-slate-400 text-sm">No emails yet</div>
              ) : (
                <Table>
                  <TableHeader><TableRow className="bg-slate-50">
                    <TableHead className="text-[10px] uppercase text-slate-500 font-semibold w-16">Type</TableHead>
                    <TableHead className="text-[10px] uppercase text-slate-500 font-semibold">Subject</TableHead>
                    <TableHead className="text-[10px] uppercase text-slate-500 font-semibold">From/To</TableHead>
                    <TableHead className="text-[10px] uppercase text-slate-500 font-semibold text-center">Status</TableHead>
                    <TableHead className="text-[10px] uppercase text-slate-500 font-semibold">Date</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {(d.recent_emails || []).map(e => (
                      <TableRow key={e.id} className={`hover:bg-indigo-50/30 ${!e.is_read && e.direction === 'incoming' ? 'bg-amber-50/30' : ''}`}
                        onClick={() => e.claim_id ? navigate(`/claims/${e.claim_id}`) : null}
                        style={{ cursor: e.claim_id ? 'pointer' : 'default' }}>
                        <TableCell>
                          <Badge className={`text-[9px] border-0 ${e.direction === 'incoming' ? 'bg-teal-100 text-teal-700' : 'bg-indigo-100 text-indigo-700'}`}>
                            {e.direction === 'incoming' ? 'IN' : 'OUT'}
                          </Badge>
                        </TableCell>
                        <TableCell><span className={`text-xs ${!e.is_read && e.direction === 'incoming' ? 'font-bold text-slate-800' : 'text-slate-600'}`}>{e.subject}</span></TableCell>
                        <TableCell><span className="text-xs text-slate-500">{e.direction === 'incoming' ? e.from_email : e.to}</span></TableCell>
                        <TableCell className="text-center"><Badge className={`text-[9px] border-0 ${e.status === 'sent' ? 'bg-emerald-100 text-emerald-700' : e.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-teal-100 text-teal-700'}`}>{e.status}</Badge></TableCell>
                        <TableCell><span className="text-xs text-slate-400">{formatDate(e.created_at)}</span></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* ========== INCOMING TAB ========== */}
      {tab === 'incoming' && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input placeholder="Search emails..." className="pl-9 h-9 text-sm" value={filters.search}
                onChange={e => setFilters(f => ({...f, search: e.target.value}))}
                onKeyDown={e => e.key === 'Enter' && loadIncoming()} data-testid="incoming-search" />
            </div>
            <Select value={filters.is_read || 'all'} onValueChange={v => setFilters(f => ({...f, is_read: v === 'all' ? '' : v}))}>
              <SelectTrigger className="w-[130px] h-9 text-sm"><SelectValue placeholder="Read status" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="false">Unread</SelectItem><SelectItem value="true">Read</SelectItem></SelectContent>
            </Select>
            <Select value={filters.linked || 'all'} onValueChange={v => setFilters(f => ({...f, linked: v === 'all' ? '' : v}))}>
              <SelectTrigger className="w-[130px] h-9 text-sm"><SelectValue placeholder="Link status" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="true">Linked</SelectItem><SelectItem value="false">Unlinked</SelectItem></SelectContent>
            </Select>
            <Button size="sm" className="h-9 bg-indigo-500 text-white text-xs" onClick={loadIncoming}>Search</Button>
          </div>

          <Card className="bg-white border border-slate-200 shadow-sm"><CardContent className="p-0">
            {loading ? <div className="flex justify-center py-10"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-500" /></div> :
            incoming.length === 0 ? <div className="text-center py-10 text-slate-400 text-sm">No incoming emails. Click "Fetch Incoming Emails" to check.</div> :
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                <TableHead className="text-[10px] uppercase text-slate-500 font-semibold w-8"></TableHead>
                <TableHead className="text-[10px] uppercase text-slate-500 font-semibold">From</TableHead>
                <TableHead className="text-[10px] uppercase text-slate-500 font-semibold">Subject</TableHead>
                <TableHead className="text-[10px] uppercase text-slate-500 font-semibold text-center">Linked</TableHead>
                <TableHead className="text-[10px] uppercase text-slate-500 font-semibold">Date</TableHead>
                <TableHead className="text-[10px] uppercase text-slate-500 font-semibold text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {incoming.map(e => (
                  <TableRow key={e.id} className={`${!e.is_read ? 'bg-amber-50/40 font-medium' : ''}`}>
                    <TableCell>{!e.is_read && <div className="h-2 w-2 rounded-full bg-amber-500" />}</TableCell>
                    <TableCell><span className="text-xs text-slate-700">{e.from_email}</span></TableCell>
                    <TableCell>
                      <p className={`text-xs ${!e.is_read ? 'font-semibold text-slate-800' : 'text-slate-600'} truncate max-w-[300px]`}>{e.subject}</p>
                      <p className="text-[10px] text-slate-400 truncate max-w-[300px]">{e.body}</p>
                      {e.attachment_names?.length > 0 && <span className="text-[9px] text-slate-400">{e.attachment_names.length} attachment(s)</span>}
                    </TableCell>
                    <TableCell className="text-center">
                      {e.claim_id ? (
                        <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[9px] cursor-pointer" onClick={() => navigate(`/claims/${e.claim_id}`)}>
                          {e.claim_number || e.claim_ref || 'Linked'}
                        </Badge>
                      ) : (
                        <Badge className="bg-slate-100 text-slate-500 border-0 text-[9px]">Unlinked</Badge>
                      )}
                    </TableCell>
                    <TableCell><span className="text-xs text-slate-400">{formatDate(e.created_at)}</span></TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {!e.is_read && (
                          <Button variant="ghost" size="sm" className="h-6 text-[10px] text-slate-500" onClick={() => handleMarkRead(e.id)}>
                            <Eye className="h-3 w-3 mr-1" /> Read
                          </Button>
                        )}
                        {e.claim_id && (
                          <Button variant="ghost" size="sm" className="h-6 text-[10px] text-indigo-600" onClick={() => navigate(`/claims/${e.claim_id}`)}>
                            View Claim
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>}
          </CardContent></Card>
          {incomingTotal > 50 && <p className="text-xs text-slate-400 text-center">Showing {incoming.length} of {incomingTotal}</p>}
        </div>
      )}

      {/* ========== IMAP SETTINGS TAB ========== */}
      {tab === 'settings' && (
        <Card className="bg-white border border-slate-200 shadow-sm max-w-xl">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold font-['Outfit'] text-slate-800 flex items-center gap-2">
              <Gear className="h-4 w-4 text-indigo-500" /> IMAP Configuration
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-xs text-slate-500">Configure IMAP settings to fetch incoming TPA emails. Supports Gmail, Outlook, and custom IMAP servers.</p>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">IMAP Host</Label>
                <Input value={imapConfig.imap_host} onChange={e => setImapConfig(c => ({...c, imap_host: e.target.value}))}
                  placeholder="imap.gmail.com" className="border-slate-200" data-testid="imap-host" /></div>
              <div><Label className="text-xs">Port</Label>
                <Input type="number" value={imapConfig.imap_port} onChange={e => setImapConfig(c => ({...c, imap_port: parseInt(e.target.value) || 993}))}
                  className="border-slate-200" data-testid="imap-port" /></div>
            </div>
            <div><Label className="text-xs">Email / Username</Label>
              <Input value={imapConfig.imap_user} onChange={e => setImapConfig(c => ({...c, imap_user: e.target.value}))}
                placeholder="claims@hospital.com" className="border-slate-200" data-testid="imap-user" /></div>
            <div><Label className="text-xs">Password / App Password</Label>
              <Input type="password" value={imapConfig.imap_password} onChange={e => setImapConfig(c => ({...c, imap_password: e.target.value}))}
                placeholder="Enter password or App Password" className="border-slate-200" data-testid="imap-password" /></div>
            <div className="flex items-center gap-2">
              <Switch checked={imapConfig.imap_use_ssl} onCheckedChange={v => setImapConfig(c => ({...c, imap_use_ssl: v}))} data-testid="imap-ssl" />
              <Label className="text-xs">Use SSL (recommended)</Label>
            </div>
            {imapConfig.last_fetch && <p className="text-[10px] text-slate-400">Last fetch: {formatDate(imapConfig.last_fetch)}</p>}
            <div className="flex gap-2 pt-2">
              <Button onClick={handleSaveImap} disabled={saving} className="flex-1 bg-indigo-500 hover:bg-indigo-600 text-white text-xs" data-testid="save-imap-btn">
                {saving ? 'Saving...' : 'Save Configuration'}
              </Button>
              <Button onClick={handleTestImap} disabled={testing} variant="outline" className="text-xs border-emerald-300 text-emerald-600" data-testid="test-imap-btn">
                {testing ? 'Testing...' : 'Test Connection'}
              </Button>
            </div>
            <div className="bg-slate-50 rounded-lg p-3 text-[10px] text-slate-500 space-y-1">
              <p className="font-semibold text-slate-600">Quick Setup Guide:</p>
              <p><strong>Gmail:</strong> Use imap.gmail.com:993 with SSL. Generate App Password at myaccount.google.com/apppasswords</p>
              <p><strong>Outlook:</strong> Use outlook.office365.com:993 with SSL.</p>
              <p><strong>Custom:</strong> Contact your IT team for IMAP host, port, and credentials.</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default EmailDashboardPage;
